/**
 * Planet data formatting utilities - API data only, no calculations
 * Shows only what comes from Swiss Ephemeris API
 */

import { devError } from '../../../../utils/devLog';
import { GrahaPosition, Coordinate, RasiName, NakshatraName } from '../../../../models/types';
import { 
  coordinateToDecimal,
  getPadaIndex
} from '../../../../utils/astrology/coordinates';
import { 
  getRashi,
  getNakshatra,
  getNavamsaRashi
} from '../../../../utils/astrology/calculations';

/**
 * Calculate local degrees within house (0-30°)
 * @param longitude Global longitude in decimal degrees
 * @returns Local degrees within the house
 */
export function calculateLocalDegreesInHouse(longitude: number): number {
  const localDegrees = longitude % 30;
  return localDegrees;
}

/**
 * Format local degrees in DMS format
 * @param localDegrees Local degrees within house (0-30)
 * @returns Formatted local degrees string
 */
export function formatLocalDegrees(localDegrees: number): string {
  const degrees = Math.floor(localDegrees);
  const minutesDecimal = (localDegrees - degrees) * 60;
  const minutes = Math.floor(minutesDecimal);
  const seconds = (minutesDecimal - minutes) * 60;

  // Pad with leading zeros: 2° 54' 19.59" -> 02° 54' 19.59"
  const degStr = degrees.toString().padStart(2, '0');
  const minStr = minutes.toString().padStart(2, '0');
  const secStr = seconds.toFixed(2).padStart(5, '0'); // 5 = "00.00"

  const formatted = `${degStr}° ${minStr}' ${secStr}"`;
  return formatted;
}

/**
 * Converts decimal longitude to precise DMS format with extended precision
 * @param decimalLongitude - Longitude in decimal degrees
 * @returns Coordinate object with high precision seconds
 */
export function decimalToPreciseCoordinate(decimalLongitude: number): Coordinate {
  // Get the fractional part within the rasi (0-30 degrees)
  const rasiPosition = decimalLongitude % 30;
  
  const degrees = Math.floor(rasiPosition);
  const minutesDecimal = (rasiPosition - degrees) * 60;
  const minutes = Math.floor(minutesDecimal);
  
  // Use high precision for seconds calculation
  const seconds = (minutesDecimal - minutes) * 60;
  
  return {
    degrees,
    minutes,
    seconds: seconds // Keep full precision from API - no rounding here
  };
}

/**
 * Formats longitude in the format: 15° Mesha 30' 45.71"
 * @param coordinate - Coordinate object with degrees, minutes, seconds
 * @param rasi - Rasi name
 * @returns Formatted longitude string with precise seconds
 */
export function formatLongitudeWithRashi(coordinate: Coordinate, rasi: RasiName): string {
  // Round to 2 decimal places and add leading zero if needed
  const roundedSeconds = Math.round(coordinate.seconds * 100) / 100;
  
  // Format with leading zero and 2 decimal places: SS.SS
  const preciseSeconds = roundedSeconds.toFixed(2).padStart(5, '0');
  
  return `${coordinate.degrees}° ${rasi} ${coordinate.minutes}' ${preciseSeconds}"`;
}

/**
 * Formats longitude directly from decimal degrees with high precision
 * @param decimalLongitude - Longitude in decimal degrees from API
 * @param rasi - Rasi name
 * @returns Formatted longitude string with precise seconds
 */
export function formatDecimalLongitudeWithRashi(decimalLongitude: number, rasi: RasiName): string {
  // Convert decimal directly to precise coordinate
  const coordinate = decimalToPreciseCoordinate(decimalLongitude);
  
  // Round to 2 decimal places and add leading zero if needed
  const roundedSeconds = Math.round(coordinate.seconds * 100) / 100;
  
  // Format with leading zero and 2 decimal places: SS.SS
  const preciseSeconds = roundedSeconds.toFixed(2).padStart(5, '0');
  
  return `${coordinate.degrees}° ${rasi} ${coordinate.minutes}' ${preciseSeconds}"`;
}

/**
 * Formats nakshatra with pada in the format: Ashwini | Pada 1
 * @param nakshatra - Nakshatra name
 * @param pada - Pada index (0-3)
 * @returns Formatted nakshatra with pada string
 */
export function formatNakshatraWithPada(nakshatra: NakshatraName, pada: number): string {
  return `${nakshatra} | Pada ${pada + 1}`;
}

/**
 * Gets full planet name from GrahaName
 * @param graha - Short graha name (e.g., "Sy", "Ch")
 * @returns Full planet name (e.g., "Surya", "Chandra")
 */
export function getFullPlanetName(graha: string): string {
  const planetNames: Record<string, string> = {
    'Sy': 'Surya',
    'Ch': 'Chandra', 
    'Ma': 'Mangala',
    'Bu': 'Budha',
    'Gu': 'Guru',
    'Sk': 'Sukra',
    'Sa': 'Shani',
    'Ra': 'Rahu',
    'Ke': 'Ketu',
    'Lg': 'Lagna'
  };
  
  return planetNames[graha] || graha;
}


/**
 * Unified planet data processing - handles both API and legacy formats
 * @param planet - Planet data from any source
 * @returns Standardized planet data object
 */
export function calculatePlanetData(planet: any): any {
  // Safety check for undefined/null input
  if (!planet || typeof planet !== 'object') {
    devError('🔍 [planetFormatters] Invalid planet data received:', planet);
    return {
      name: 'Unknown',
      short_name: 'Unknown',
      longitude: 0,
      longitude_dms: '0° Unknown 0\' 0.00"',
      latitude: 0,
      speed: 0,
      is_retrograde: false,
      rasi_number: 1,
      rasi_name: 'Unknown',
      nakshatra_number: 1,
      nakshatra_name: 'Unknown',
      nakshatra_pada: 1,
      navamsa_rasi: 'Unknown',
      fullName: 'Unknown',
      decimal: 0,
      position: '0° Unknown 0\' 0.00"',
      nakshatraPada: 'Unknown | Pada 1',
      isRetrograde: false,
      metadata: {}
    };
  }

  
  // NEW: Check for unified positionString format first
  if (planet.positionString) {
    const parsedPosition = parsePositionString(planet.positionString);
    if (parsedPosition) {
      const rasiName = getRasiNameFromNumber(parsedPosition.rasiNumber) as RasiName;
      const coordinate = {
        degrees: Math.floor(parsedPosition.degrees),
        minutes: Math.floor((parsedPosition.degrees % 1) * 60),
        seconds: ((parsedPosition.degrees % 1) * 60 % 1) * 60
      };
      
      return {
        name: planet.name || getFullPlanetName(planet.graha || planet.short_name),
        short_name: planet.short_name || planet.graha,
        longitude: parsedPosition.longitude,
        longitude_dms: formatLongitudeWithRashi(coordinate, rasiName),
        latitude: 0,
        speed: parsedPosition.speed,
        is_retrograde: parsedPosition.speed < 0,
        rasi_number: parsedPosition.rasiNumber + 1, // Convert from 0-11 to 1-12
        rasi_name: rasiName,
        nakshatra_number: planet.nakshatra_number ?? 1,
        nakshatra_name: planet.nakshatra_name || 'Unknown',
        nakshatra_pada: planet.nakshatra_pada || 1,
        navamsa_rasi: planet.navamsa_rasi || 'Unknown',
        
        // Legacy compatibility fields removed - no longer needed
        metadata: planet.metadata || {},
        positionString: planet.positionString // Preserve unified format
      };
    }
  }

  // Check if this is already API format (has longitude, rasi_name, etc.)
  if (planet.longitude !== undefined && planet.rasi_name !== undefined) {
    // Direct API format - use as is
    return {
      name: planet.name || getFullPlanetName(planet.graha || planet.short_name),
      short_name: planet.short_name || planet.graha,
      longitude: planet.longitude || 0,
      longitude_dms: planet.longitude_dms || '0° 0\' 0.00"',
      latitude: planet.latitude ?? 0,
      speed: planet.speed ?? 0,
      is_retrograde: planet.is_retrograde || false,
      rasi_number: planet.rasi_number ?? 1,
      rasi_name: planet.rasi_name || 'Unknown',
      nakshatra_number: planet.nakshatra_number ?? 1,
      nakshatra_name: planet.nakshatra_name || 'Unknown',
      nakshatra_pada: planet.nakshatra_pada || 1,
      navamsa_rasi: planet.navamsa_rasi || 'Unknown',
      muhurta_num: planet.muhurta_num,
      muhurta_graha: planet.muhurta_graha,
      muhurta_lord: planet.muhurta_lord,
      
      // Legacy compatibility fields removed - no longer needed
      metadata: planet.metadata || {}
    };
  }
  
  // Check for API data in metadata (required!)
  const apiPosition = planet.metadata?.api_data;
  
  if (apiPosition) {
    // Found API data - use it
    return calculatePlanetData(apiPosition);
  }
  
  // ERROR: No API data found - this should not happen!
  devError(`❌ [planetFormatters] ERROR: No API data found for ${planet.graha}!`, {
    planetStructure: Object.keys(planet),
    hasMetadata: !!planet.metadata,
    metadataKeys: planet.metadata ? Object.keys(planet.metadata) : [],
    hasApiData: !!(planet.metadata?.api_data),
    planet: planet
  });
  const decimal = coordinateToDecimal(planet.position);
  return {
    name: getFullPlanetName(planet.graha),
    short_name: planet.graha,
    longitude: decimal,
    longitude_dms: formatDecimalLongitudeWithRashi(decimal, planet.rasi || 'Unknown'),
    latitude: 0,
    speed: 0,
    is_retrograde: planet.isRetrograde || false,
    rasi_number: Math.floor(decimal / 30) + 1,
    rasi_name: planet.rasi || 'Unknown',
    nakshatra_number: 1,
    nakshatra_name: planet.nakshatra || 'Unknown',
    nakshatra_pada: 1,
    navamsa_rasi: 'Unknown',
    
    // Legacy compatibility fields removed - no longer needed
    metadata: planet.metadata || {}
  };
}

/**
 * Formats complete planet summary for accordion level 1
 * @param planet - Planet position data
 * @returns Formatted planet summary string
 */
export function formatPlanetSummary(planet: GrahaPosition): string {
  const decimal = coordinateToDecimal(planet.position);
  const rasi = getRashi(decimal);
  const nakshatra = getNakshatra(decimal);
  const padaIndex = getPadaIndex(decimal);
  const navamsaRashi = getNavamsaRashi(decimal);
  
  const fullName = getFullPlanetName(planet.graha);
  const longitude = formatDecimalLongitudeWithRashi(decimal, rasi);
  const nakshatraPada = formatNakshatraWithPada(nakshatra, padaIndex);
  const navamsa = `Navamsa: ${navamsaRashi}`;
  const rasiInfo = `Rasi: ${rasi}`;
  
  return `${fullName} | ${longitude} | ${nakshatraPada} | ${navamsa} | ${rasiInfo}`;
}

/**
 * Sorts planets in traditional order
 * @param planets - Array of planet positions
 * @returns Sorted array of planets
 */
export function sortPlanetsInTraditionalOrder(planets: GrahaPosition[]): GrahaPosition[] {
  const order = ['Lg', 'Sy', 'Ch', 'Ma', 'Bu', 'Gu', 'Sk', 'Sa', 'Ra', 'Ke']; // Lagna first, then planets
  
  const sorted = planets.sort((a, b) => {
    const indexA = order.indexOf(a.graha);
    const indexB = order.indexOf(b.graha);
    
    // If planet not in order array, put it at the end
    if (indexA === -1) return 1;
    if (indexB === -1) return -1;
    
    return indexA - indexB;
  });
  
  return sorted;
}

/**
 * Formats planet position for display
 * @param planet - Planet position data
 * @returns Formatted position string
 */
export function formatPlanetPosition(planet: GrahaPosition): string {
  const calculated = calculatePlanetData(planet);
  return calculated.position;
}

/**
 * Gets planet pada number
 * @param planet - Planet position data
 * @returns Pada number
 */
export function getPlanetPada(planet: GrahaPosition): number {
  const calculated = calculatePlanetData(planet);
  return calculated.nakshatra_pada;
}

/**
 * Parse position string in unified format "rasi|degrees|longitude|speed"
 * @param positionString - Position string from API or animation
 * @returns Parsed position data or null if invalid
 */
function parsePositionString(positionString: string): { rasiNumber: number; degrees: number; longitude: number; speed: number } | null {
  if (!positionString || typeof positionString !== 'string') {
    return null;
  }

  const parts = positionString.split('|');
  if (parts.length !== 4) {
    return null;
  }

  try {
    const rasiNumber = parseInt(parts[0], 10);
    const degrees = parseFloat(parts[1]);
    const longitude = parseFloat(parts[2]);
    const speed = parseFloat(parts[3]);

    if (isNaN(rasiNumber) || isNaN(degrees) || isNaN(longitude) || isNaN(speed)) {
      return null;
    }

    return { rasiNumber, degrees, longitude, speed };
  } catch (error) {
    return null;
  }
}

/**
 * Get rasi name from number (0-11 index)
 * @param rasiNumber - Rasi index (0-11)
 * @returns Rasi name
 */
function getRasiNameFromNumber(rasiNumber: number): string {
  const rasiNames = [
    'Mesha',      // 0
    'Vrishabha',  // 1
    'Mithuna',    // 2
    'Karka',      // 3
    'Simha',      // 4
    'Kanya',      // 5
    'Tula',       // 6
    'Vrishchika', // 7
    'Dhanu',      // 8
    'Makara',     // 9
    'Kumbha',     // 10
    'Meena'       // 11
  ];

  return rasiNames[rasiNumber] || 'Unknown';
}